import React from 'react';

import { RouteAnnouncer } from '@lumapps/a11y/components/RouteAnnouncer';
import { Customizations } from '@lumapps/customizations';
import { AdvancedDebugInfo, AdvancedDebugInfoProps } from '@lumapps/debug';
import { isFeatureEnabled } from '@lumapps/features';
import { NotificationStack } from '@lumapps/notifications';
import { useSelector } from '@lumapps/redux/react';

export interface InnerShellProps {
    children: React.ReactNode;
    /** should the notifications stack be displayed or not */
    shouldDisplayNotifications?: boolean;
    /** should the advanced debug info dialog be displayed or not */
    shouldDisplayAdvancedDebugInfo?: boolean | Partial<AdvancedDebugInfoProps>;
    /** whether customizations should be displayed or not. This can be either a boolean or a string which will map to a specific feature flag. */
    shouldDisplayCustomizations?: boolean | string;
}

/**
 * Component that wraps the inner shell of the application.
 * It includes notifications, debug info, and customizations.
 * Do not use this component directly, use the `Shell` component instead, or `AppShell` if you are creating an app.
 * @param InnerShellProps
 * @returns InnerShell
 */
export const InnerShell: React.FC<InnerShellProps> = ({
    children,
    shouldDisplayNotifications = true,
    shouldDisplayAdvancedDebugInfo = true,
    shouldDisplayCustomizations = false,
}) => {
    const areCustomizationsEnabled = useSelector(
        isFeatureEnabled(typeof shouldDisplayCustomizations === 'string' ? shouldDisplayCustomizations : ''),
    );
    const CustomizationsWrapper =
        shouldDisplayCustomizations === true || areCustomizationsEnabled ? Customizations : React.Fragment;

    return (
        <CustomizationsWrapper>
            {shouldDisplayNotifications ? <NotificationStack /> : undefined}
            <RouteAnnouncer />
            {children}
            {shouldDisplayAdvancedDebugInfo ? (
                <AdvancedDebugInfo
                    showModes
                    showFeatureFlags
                    {...(typeof shouldDisplayAdvancedDebugInfo === 'object' ? shouldDisplayAdvancedDebugInfo : {})}
                />
            ) : undefined}
        </CustomizationsWrapper>
    );
};
